/** @module Auth0SentryReporter */
/* eslint-disable import/extensions */
import Auth0DynamicScriptLoader from './auth0-dynamic-script-loader.mjs';
import Auth0UtilsBrowser from './auth0-utils-browser.mjs';
import logger from './logging/auth0-logger.mjs';

const sentryPackageUrl = 'https://browser.sentry-cdn.com/9.15.0/bundle.min.js';

class Auth0SentryReporter {
  constructor(tenantOrDomain, sentryDsn) {
    const resolved = Auth0UtilsBrowser.tenantOrDomainResolver(tenantOrDomain);
    this._tenant = resolved.tenant.id;
    this._tenantClass = resolved.tenant.tenantClass ?? null;
    this._scriptLoader = new Auth0DynamicScriptLoader(tenantOrDomain);
    this._sentryDsn = sentryDsn;
  }

  static reporterFactory = async (tenant, sentryDsn, ctx = null) => {
    try {
      console.log(`Sentry reporterFactory for ${tenant} @ ${sentryDsn}`);
      const newReporter = new Auth0SentryReporter(tenant, sentryDsn);
      await newReporter._init(ctx);
      console.log('Sentry initialization success');
      return newReporter;
    } catch (error) {
      logger.error(`Sentry initialization has failed! ${JSON.stringify(error)}`);
    }
  };

  _init = async (ctx = null) => {
    await this._scriptLoader.loadScripts({ url: sentryPackageUrl, attributes: { crossorigin: 'anonymous' }, required: false });

    // DO NOT REMOVE; MUSTACHE VAR OVERRIDE; 
    const ver = '71c5d67';

    if (this._sentryDsn) {
      console.log(`Initializing sentry @ ${this._sentryDsn}...`);
      window.Sentry.init({
        dsn: this._sentryDsn,
        release: ver,
        sendDefaultPii: true,
      });

      // Set tenant_class as a global tag for filtering/searching in Sentry
      if (this._tenantClass) {
        window.Sentry.setTag('tenant_class', this._tenantClass);
      }

      if (ctx) this.setContext(ctx);
    }
  };

  setContext = (ctx, ctxCaller = null) => {
    logger.info(`Auth0SentryReporter context: ${JSON.stringify(ctx)}`);
    if (!window.Sentry) {
      logger.warn('Auth0SentryReporter error in setContext(). Reporter failed to initialize.');
      return;
    }
    const key = ctxCaller || 'auth0Context';
    window.Sentry.setContext(key, ctx);
  };

  captureException = (exception) => {
    logger.error('Auth0SentryReporter exception', exception);
    if (!window.Sentry) {
      logger.warn('Auth0SentryReporter error in captureException(). Reporter failed to initialize.');
      return;
    }
    window.Sentry.captureException(exception);
  };

  captureMessage = (message) => {
    logger.info(`Auth0SentryReporter message: ${message}`);
    if (!window.Sentry) {
      logger.warn('Auth0SentryReporter error in captureMessage(). Reporter failed to initialize.');
      return;
    }

    window.Sentry.captureMessage(message);
  };
}

export default Auth0SentryReporter;
